import React, { memo, useState, useRef, useEffect } from "react";
import styled from "styled-components";
import ProfileImage from "../../Componets/ProfileImage";
const Constant = require("../Constant");

const MessageBubble = memo(
  ({
    message,
    isOwn,
    showAvatar,
    status,
    onEdit,
    onDelete,
    canEdit,
    canDelete,
  }) => {
    const [showActions, setShowActions] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const actionsRef = useRef(null);
    const modalRef = useRef(null);

    // Format timestamp
    const formatTime = (timestamp) => {
      if (!timestamp) return "";
      return new Date(timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    // Get message status (prefer provided status from parent if available)
    const getMessageStatus = () => {
      if (status) return status;
      if (!message.readBy || message.readBy.length <= 1) {
        return "sent"; // Single checkmark
      }
      return "read"; // Double checkmark
    };

    const messageStatus = getMessageStatus();
    const messageTime = formatTime(message.createdAt);

    // Handle three dots menu hover
    const handleMouseEnter = () => {
      if (isOwn && !message.isDeleted && (canEdit || canDelete)) {
        setShowActions(true);
      }
    };

    const handleMouseLeave = () => {
      setShowActions(false);
    };

    // Handle three dots click
    const handleDotsClick = (e) => {
      e.stopPropagation();
      if (isOwn && !message.isDeleted && (canEdit || canDelete)) {
        setShowModal(true);
      }
    };

    // Close modal
    const closeModal = () => {
      setShowModal(false);
    };

    // Handle edit action
    const handleEdit = () => {
      if (canEdit && onEdit) {
        onEdit(message);
        closeModal();
      }
    };

    // Handle delete action
    const handleDelete = () => {
      if (canDelete && onDelete) {
        onDelete(message);
        closeModal();
      }
    };

    // Handle clicks outside modal
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
          setShowModal(false);
        }
      };

      if (showModal) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [showModal]);

    // Proxy URL function for profile pictures
    const proxiedUrl = (url) => {
      if (!url || url.includes("story-tent-testing.s3.ap-south-1.amazonaws.com")) return url;
      return `${Constant.apiBasePath}proxy-image?url=${encodeURIComponent(url)}`;
    };

    // Handle deleted messages
    if (message.isDeleted) {
      return (
        <MessageContainer $isOwn={isOwn}>
          {!isOwn && showAvatar && (
            <AvatarContainer>
              <ProfileImage
                src={message.sender?.profilePic}
                name={message.sender?.name || "U"}
                size={32}
                alt={message.sender?.name || "User"}
                proxiedUrl={proxiedUrl}
                avatarProps={{
                  border: "2px solid var(--color-surface)",
                }}
                imgStyle={{
                  border: "2px solid var(--color-surface)",
                }}
              />
            </AvatarContainer>
          )}

          <MessageContent $isOwn={isOwn}>
            <Bubble $isOwn={isOwn} $isDeleted={true}>
              <MessageText $isOwn={isOwn} $isDeleted={true}>
                This message was deleted
              </MessageText>
            </Bubble>
          </MessageContent>
        </MessageContainer>
      );
    }

    return (
      <MessageContainer $isOwn={isOwn}>
        {!isOwn && showAvatar && (
          <AvatarContainer>
            <ProfileImage
              src={message.sender?.profilePic}
              name={message.sender?.name || "U"}
              size={32}
              alt={message.sender?.name || "User"}
              proxiedUrl={proxiedUrl}
              avatarProps={{
                border: "2px solid var(--color-surface)",
              }}
              imgStyle={{
                border: "2px solid var(--color-surface)",
              }}
            />
          </AvatarContainer>
        )}

        <MessageContent $isOwn={isOwn}>
          <BubbleRow
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {isOwn && (canEdit || canDelete) && (
              <ActionContainer>
                {showActions && (
                  <ActionTrigger
                    onClick={handleDotsClick}
                    aria-label="Open actions"
                  >
                    <ThreeDotsIcon>⋯</ThreeDotsIcon>
                  </ActionTrigger>
                )}
                {showModal && (
                  <ActionsPopover ref={modalRef}>
                    {canEdit && (
                      <IconButton onClick={handleEdit} aria-label="Edit">
                        <IconPencil>✏️</IconPencil>
                      </IconButton>
                    )}
                    {canDelete && (
                      <IconButton
                        $isDelete
                        onClick={handleDelete}
                        aria-label="Delete"
                      >
                        <IconTrash>🗑️</IconTrash>
                      </IconButton>
                    )}
                  </ActionsPopover>
                )}
              </ActionContainer>
            )}

            <Bubble $isOwn={isOwn} $hasError={message.decryptError}>
              <MessageText $isOwn={isOwn} $hasError={message.decryptError}>
                {message.decryptError ? (
                  <ErrorMessage>
                    <ErrorIcon>⚠</ErrorIcon>
                    Failed to decrypt message
                  </ErrorMessage>
                ) : (
                  message.decryptedContent || "Loading..."
                )}
              </MessageText>

              <MessageFooter $isOwn={isOwn}>
                <TimeText $isOwn={isOwn}>{messageTime}</TimeText>
                {message.isEdited && <EditedLabel $isOwn={isOwn}>Edited</EditedLabel>}
                {isOwn && (
                  <StatusIcon $status={messageStatus}>
                    {messageStatus === "sent" ? "✓" : "✓✓"}
                  </StatusIcon>
                )}
              </MessageFooter>
            </Bubble>
          </BubbleRow>
        </MessageContent>
      </MessageContainer>
    );
  }
);

MessageBubble.displayName = "MessageBubble";

export default MessageBubble;

// Styled Components
const MessageContainer = styled.div`
  display: flex;
  align-items: flex-end;
  margin-bottom: 8px;
  padding: 0 20px;
  flex-direction: ${(props) => (props.$isOwn ? "row-reverse" : "row")};
`;

const AvatarContainer = styled.div`
  margin-right: 8px;
  margin-bottom: 4px;
  flex-shrink: 0;
`;

// Avatar and DefaultAvatar are now handled by ProfileImage component

const MessageContent = styled.div`
  max-width: 70%;
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$isOwn ? "flex-end" : "flex-start")};
`;

const Bubble = styled.div`
  background-color: ${(props) =>
    props.$isDeleted
      ? "var(--color-surface)"
      : props.$isOwn
      ? props.$hasError
        ? "var(--color-error)" // Or a light red from theme if available, sticking to semantic meaning
        : "var(--color-text)" // Sent message: dark/black (light theme) -> white text
      : props.$hasError
      ? "var(--color-surface)"
      : "var(--color-surface)"}; // Received message: white/surface -> dark text
  
  color: ${(props) =>
    props.$isDeleted
      ? "var(--color-text-secondary)"
      : props.$isOwn
      ? "var(--color-surface)" // White text on dark bubble
      : "var(--color-text-tertiary)"}; // Black text on white bubble (using text-tertiary as requested)

  border: ${(props) =>
    props.$isDeleted
      ? "1px solid var(--color-border)"
      : props.$isOwn
      ? "none"
      : props.$hasError
      ? "1px solid var(--color-error)"
      : "1px solid var(--color-border)"};
      
  border-radius: ${(props) =>
    props.$isOwn ? "18px 18px 4px 18px" : "18px 18px 18px 4px"};
  padding: 12px 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  word-wrap: break-word;
  word-break: break-word;
  position: relative;
  user-select: none;
`;

const MessageText = styled.div`
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
  white-space: pre-wrap;
  color: inherit;
  font-style: ${(props) => (props.$isDeleted ? "italic" : "normal")};
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-style: italic;
`;

const ErrorIcon = styled.span`
  font-size: 12px;
`;

const MessageFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$isOwn ? "flex-end" : "flex-start")};
  margin-top: 4px;
  gap: 4px;
`;

const TimeText = styled.span`
  font-size: 11px;
  opacity: 0.7;
  color: inherit; 
`;

const StatusIcon = styled.span`
  font-size: 12px;
  color: ${(props) => (props.$status === "read" ? "var(--color-surface)" : "inherit")};
  font-weight: 600;
  opacity: ${(props) => (props.$status === "read" ? "1" : "0.7")};
`;

const EditedLabel = styled.span`
  font-size: 10px;
  color: inherit;
  opacity: 0.7;
  font-style: italic;
  margin-left: 4px;
`;

const BubbleRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 8px;
  position: relative;
`;

const ActionContainer = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionTrigger = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);

  &:hover {
    background: var(--color-background);
    border-color: var(--color-text-secondary);
    transform: scale(1.05);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }

  &:active {
    transform: scale(1);
  }
`;

const ThreeDotsIcon = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: var(--color-text-primary);
  line-height: 1;
`;

const ActionsPopover = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 20;
  display: flex;
  flex-direction: column;
  padding: 6px;
  gap: 6px;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--color-background);
  }
`;

const IconPencil = styled.span`
  font-size: 18px;
  color: var(--color-text);
`;

const IconTrash = styled.span`
  font-size: 18px;
  color: var(--color-error, #dc3545);
`;
